import { useEffect, useState } from "react";
import {
  AssessmentAction,
  type AssessmentResponse,
  type AssessmentUnavailableReason,
} from "../network/useAssessment";
import {
  isRecognizedAssessmentGenerationStatus,
  useAssessmentGenerationStatus,
} from "../network/useAssessmentGenerationStatus";
import { useStartAssessmentGeneration } from "../network/useStartAssessmentGeneration";

export const useAssessmentGeneration = ({
  assessmentData,
  encounterId,
  enabled,
  motherEncounterId,
  patientId,
}: {
  assessmentData?: AssessmentResponse;
  encounterId?: string;
  enabled: boolean;
  motherEncounterId?: string;
  patientId?: string;
}) => {
  const [expectedWorkflowId, setExpectedWorkflowId] = useState<string>();
  const startAssessmentGeneration = useStartAssessmentGeneration({
    patientId,
    encounterId,
    motherEncounterId,
  });
  const generationStatus = useAssessmentGenerationStatus({
    patientId,
    encounterId,
    expectedWorkflowId,
    enabled,
  });
  const activeGeneration = enabled
    ? {
        data: generationStatus.data,
        expectedWorkflowId,
        generateAction: assessmentData?.availableActions?.availableActions.find(
          ({ action }) => action === AssessmentAction.GENERATE,
        ),
        isFetching: generationStatus.isFetching,
        isStartPending: startAssessmentGeneration.isPending,
      }
    : undefined;
  const status = isRecognizedAssessmentGenerationStatus(
    activeGeneration?.data?.status,
  )
    ? activeGeneration.data.status
    : undefined;
  const hasObservedExpectedWorkflow =
    activeGeneration?.expectedWorkflowId != null &&
    activeGeneration.data?.workflowId === activeGeneration.expectedWorkflowId &&
    status != null;
  const hasObservedExpectedWorkflowTerminalStatus =
    hasObservedExpectedWorkflow &&
    (status === "COMPLETED" || status === "FAILED");

  useEffect(() => {
    if (
      expectedWorkflowId == null ||
      !hasObservedExpectedWorkflowTerminalStatus
    ) {
      return;
    }

    setExpectedWorkflowId(undefined);
  }, [expectedWorkflowId, hasObservedExpectedWorkflowTerminalStatus]);

  const generateAction = activeGeneration?.generateAction;
  const unavailableReason = generateAction?.unavailableReason ?? undefined;
  const isAwaitingExpectedWorkflow =
    activeGeneration?.expectedWorkflowId != null &&
    !hasObservedExpectedWorkflow;
  const isInProgress =
    activeGeneration != null &&
    (activeGeneration.isStartPending ||
      isAwaitingExpectedWorkflow ||
      status === "RUNNING");
  const hasAssessment = assessmentData?.assessment != null;
  const hasPreviouslyGeneratedAssessment = Boolean(
    status === "COMPLETED" &&
      activeGeneration?.data?.workflowId &&
      hasAssessment,
  );
  const isAssessmentFlagged = activeGeneration?.data?.flagged === true;
  const shouldShowHigherReasoningNotice =
    status === "RUNNING" &&
    activeGeneration?.data?.higherReasoningRequested === true &&
    (activeGeneration.expectedWorkflowId == null ||
      activeGeneration.data.workflowId === activeGeneration.expectedWorkflowId);

  const showControls =
    activeGeneration != null &&
    generateAction != null &&
    (!activeGeneration.isFetching || isInProgress || status === "COMPLETED");

  const start = async () => {
    if (!showControls || unavailableReason != null || isInProgress) {
      return;
    }

    const { workflowId } = await startAssessmentGeneration.mutateAsync();
    setExpectedWorkflowId(workflowId);
  };

  return {
    feedbackBtSpanId:
      showControls && !isInProgress && (status !== "COMPLETED" || hasAssessment)
        ? activeGeneration?.data?.btSpanId
        : undefined,
    hasPreviouslyGeneratedAssessment,
    isAssessmentFlagged,
    isInProgress,
    shouldShowHigherReasoningNotice,
    showControls,
    start,
    status,
    unavailableReason: unavailableReason as
      | AssessmentUnavailableReason
      | undefined,
  };
};
